import * as THREE from "three"

import {
  clampNumber,
  parseFloatOr,
  parseIntegerOr
} from "./shared"

const GENERIC_NODE_NAME_PATTERN = /^(mesh|mesh_\d+|node|node_\d+|object|object_\d+)$/i

export const hasUsefulNodeName = (object) => {
  const name = object?.name?.toString().trim()
  return Boolean(name) && !GENERIC_NODE_NAME_PATTERN.test(name)
}

export const measureGroundSnapOffsetY = (object3D) => {
  object3D.updateMatrixWorld(true)

  let meshBox = null
  object3D.traverse((child) => {
    if (!child.visible || !child.isMesh || !child.geometry) return

    const childBox = new THREE.Box3().setFromObject(child)
    if (childBox.isEmpty()) return

    if (meshBox) meshBox.union(childBox)
    else meshBox = childBox.clone()
  })

  const box = meshBox || new THREE.Box3().setFromObject(object3D)
  return Number.isFinite(box.min.y) ? -box.min.y : 0
}

export const snapModelToGround = (
  object3D,
  { baseY = object3D.position.y, groundOffsetY = measureGroundSnapOffsetY(object3D) } = {}
) => {
  // Box3 walks the full world-space hierarchy, so nested GLTF children and arbitrary origins are included.
  object3D.position.y = baseY + groundOffsetY
  object3D.updateMatrixWorld(true)
  return groundOffsetY
}

const frameBox = (camera, box) => {
  const size = box.getSize(new THREE.Vector3())
  const center = box.getCenter(new THREE.Vector3())
  const sphere = box.getBoundingSphere(new THREE.Sphere())
  const maxDimension = Math.max(size.x, size.y, size.z) || 1
  const fov = camera.fov * (Math.PI / 180)
  let distance = Math.abs(maxDimension / Math.sin(fov / 2))
  distance *= 0.27

  camera.near = Math.max(distance / 100, 0.1)
  camera.far = distance * 100
  camera.updateProjectionMatrix()

  return { box, size, center, sphere, maxDimension, distance }
}

export const frameObject = (camera, object) => frameBox(camera, new THREE.Box3().setFromObject(object))

export const defaultSceneFraming = (camera) =>
  frameBox(
    camera,
    new THREE.Box3(
      new THREE.Vector3(-10, 0, -10),
      new THREE.Vector3(10, 4, 10)
    )
  )

export const buildTransparentCablingPlanTextureFromImage = (image, settings) => {
  const width = image?.naturalWidth || image?.width
  const height = image?.naturalHeight || image?.height

  if (!width || !height) {
    throw new Error("Reference layer image dimensions could not be read.")
  }

  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height

  const context = canvas.getContext("2d", { willReadFrequently: true })
  context.drawImage(image, 0, 0, width, height)

  const imageData = context.getImageData(0, 0, width, height)
  const pixels = imageData.data
  const threshold = settings.whiteThreshold
  const feather = settings.whiteFeather
  const featherStart = threshold - feather
  const MAX_COLOR_DISTANCE = Math.sqrt(3 * 255 * 255)

  for (let index = 0; index < pixels.length; index += 4) {
    const red = pixels[index]
    const green = pixels[index + 1]
    const blue = pixels[index + 2]
    const luminance = (0.2126 * red) + (0.7152 * green) + (0.0722 * blue)
    const maxChannel = Math.max(red, green, blue)
    const minChannel = Math.min(red, green, blue)
    const saturation = maxChannel === 0 ? 0 : (maxChannel - minChannel) / maxChannel
    const distanceToWhite = Math.sqrt(((255 - red) ** 2) + ((255 - green) ** 2) + ((255 - blue) ** 2))
    const closenessToWhite = 1 - clampNumber(distanceToWhite / MAX_COLOR_DISTANCE, 0, 1)
    let alphaMultiplier = 1

    if (luminance >= threshold) {
      alphaMultiplier = 0
    } else if (feather > 0 && luminance > featherStart) {
      alphaMultiplier = (threshold - luminance) / feather
      const edgeAlphaFloor = clampNumber(0.08 + ((1 - saturation) * 0.14), 0.08, 0.22)
      alphaMultiplier = Math.max(alphaMultiplier, edgeAlphaFloor)
    }

    if (closenessToWhite > 0.6) {
      alphaMultiplier *= (1 - ((closenessToWhite - 0.6) / 0.4) * 0.22)
    }

    if (saturation > 0.2 && luminance > featherStart) {
      alphaMultiplier *= 0.86
    }

    const clampedAlphaMultiplier = clampNumber(alphaMultiplier, 0, 1)

    if (clampedAlphaMultiplier > 0 && clampedAlphaMultiplier < 1 && luminance > featherStart) {
      const whiteMatteContribution = (1 - clampedAlphaMultiplier) * 255
      pixels[index] = clampNumber((red - whiteMatteContribution) / clampedAlphaMultiplier, 0, 255)
      pixels[index + 1] = clampNumber((green - whiteMatteContribution) / clampedAlphaMultiplier, 0, 255)
      pixels[index + 2] = clampNumber((blue - whiteMatteContribution) / clampedAlphaMultiplier, 0, 255)
    }

    pixels[index + 3] = Math.round(pixels[index + 3] * clampedAlphaMultiplier)
  }

  context.putImageData(imageData, 0, 0)

  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.minFilter = THREE.LinearFilter
  texture.magFilter = THREE.LinearFilter
  texture.needsUpdate = true
  return texture
}

export const buildTransparentCablingPlanTexture = async (imageUrl, settings) => {
  const sourceTexture = await new THREE.TextureLoader().loadAsync(imageUrl)

  try {
    return {
      texture: buildTransparentCablingPlanTextureFromImage(sourceTexture.image, settings),
      image: sourceTexture.image
    }
  } finally {
    sourceTexture.dispose()
  }
}

export const buildNodeKey = (object, root) => {
  const segments = []
  let current = object

  while (current && current !== root) {
    const parent = current.parent
    const index = parent ? parent.children.indexOf(current) : 0
    const label = (current.name || current.type || "node")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "") || "node"

    segments.unshift(`${label}-${index}`)
    current = parent
  }

  return segments.join("/")
}

export const resolveSelectableObject = (object, root) => {
  let current = object
  let fallback = object

  while (current && current !== root) {
    fallback = current
    if (hasUsefulNodeName(current) || current.userData?.external_key || current.userData?.externalKey) {
      return current
    }

    current = current.parent
  }

  return fallback
}

export const resolvePlacementRoot = (object) => {
  let current = object

  while (current) {
    if (current.userData?.dtPlacementId) return current
    current = current.parent
  }

  return null
}

export const resolveNavigationPointRoot = (object) => {
  let current = object
  let navigationRoot = null

  while (current) {
    if (current.userData?.dtNavigationPointId) navigationRoot = current
    current = current.parent
  }

  return navigationRoot
}

export const normalizeNavigationPoint = (payload = {}) => ({
  id: payload.id,
  sort_order: parseIntegerOr(payload.sort_order, 0),
  position: {
    x: parseFloatOr(payload.position?.x, 0),
    y: parseFloatOr(payload.position?.y, 0),
    z: parseFloatOr(payload.position?.z, 0)
  },
  rotation: {
    x: parseFloatOr(payload.rotation?.x, 0),
    y: parseFloatOr(payload.rotation?.y, 0),
    z: parseFloatOr(payload.rotation?.z, 0)
  },
  metadata: payload.metadata || {}
})
